/** Einheit contract for all five "+ Artikel hinzufügen" catalog endpoints (14.10.2026). */
export interface ArtikelEinheitDto {
  readonly id: number;
  readonly name: string;
  /** How many BaseEinheitId units form one unit (NOT necessarily the smallest unit). */
  readonly menge?: number;
  readonly baseEinheitId?: number | null;
  /** Sort order from dbo.Einheiten.Nr. */
  readonly nr?: number | null;
  /** The article's Grundeinheit from dbo.Artikel. */
  readonly isDefault?: boolean;
}

export interface ArtikelEinheit {
  readonly id: number;
  readonly name: string;
  readonly menge: number;
  readonly baseEinheitId: number | null;
  readonly nr: number | null;
  readonly isDefault: boolean;
  /** Total number of smallest units in one unit, including intermediate parents. */
  readonly faktorZurKleinstenEinheit: number;
}

/** Sort, deduplicate and resolve potentially multi-step unit conversion chains. */
export function normalizeArtikelEinheiten(items: readonly ArtikelEinheitDto[] | null | undefined): ArtikelEinheit[] {
  const byId = new Map<number, ArtikelEinheitDto>();
  for (const item of items ?? []) {
    if (!item || !Number.isSafeInteger(item.id) || item.id < 0 || !item.name?.trim()) continue;
    if (!byId.has(item.id)) byId.set(item.id, item);
  }

  const factors = new Map<number, number>();
  const resolve = (id: number, visiting: Set<number>): number => {
    const found = factors.get(id);
    if (found !== undefined) return found;
    const item = byId.get(id);
    if (!item || visiting.has(id)) return 1; // malformed/cyclic data: never multiply infinitely
    const next = new Set(visiting);
    next.add(id);
    const menge = item.menge !== undefined && Number.isFinite(item.menge) && item.menge > 0
      ? item.menge : 1;
    const baseId = item.baseEinheitId;
    const factor = baseId != null && baseId !== id && byId.has(baseId)
      ? menge * resolve(baseId, next)
      : 1;
    const safeFactor = Number.isFinite(factor) && factor > 0 ? factor : 1;
    factors.set(id, safeFactor);
    return safeFactor;
  };

  return [...byId.values()]
    .sort((a, b) => (a.nr ?? Number.MAX_SAFE_INTEGER) - (b.nr ?? Number.MAX_SAFE_INTEGER))
    .map((item) => ({
      id: item.id,
      name: item.name.trim(),
      menge: item.menge !== undefined && Number.isFinite(item.menge) && item.menge > 0 ? item.menge : 1,
      baseEinheitId: item.baseEinheitId ?? null,
      nr: item.nr ?? null,
      isDefault: item.isDefault === true,
      faktorZurKleinstenEinheit: resolve(item.id, new Set<number>()),
    }));
}

/** Choose the backend default for new rows; keep a saved unit for existing rows. */
export function waehleArtikelEinheit<T extends { readonly id?: number; readonly isDefault?: boolean }>(
  units: readonly T[], savedUnitId?: number | null
): T | undefined {
  return (savedUnitId != null ? units.find((unit) => unit.id === savedUnitId) : undefined)
    ?? units.find((unit) => unit.isDefault === true)
    ?? units[0];
}
